if (!customElements.get('disclosure-list')) {
  customElements.define(
    'disclosure-list',
    class DisclosureList extends HTMLElement {
      static get observedAttributes() {
        return ['data-select', 'data-value'];
      }

      constructor() {
        super();
        this.handleButtonClick = this.handleButtonClick.bind(this);
        this.handleItemClick = this.handleItemClick.bind(this);
        this.handleItemKeyDown = this.handleItemKeyDown.bind(this);
        this.handleButtonKeyDown = this.handleButtonKeyDown.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleSearchInput = this.handleSearchInput.bind(this);
        this.handleSearchKeyDown = this.handleSearchKeyDown.bind(this);
      }

      connectedCallback() {
        this.input = this.querySelector('input[type="hidden"]');
        this.button = this.querySelector('button');
        this.panel = this.querySelector('ul');
        this.items = Array.from(this.querySelectorAll('li.disclosure__item'));
        this.filterable = this.hasAttribute('data-filterable');

        if (!this.input || !this.panel) return;

        if (this.filterable) {
          this.searchInput = this.panel.querySelector('.disclosure__search-input');
          this.emptyState = this.panel.querySelector('.disclosure__empty');

          if (this.searchInput) {
            this.searchInput.addEventListener('input', this.handleSearchInput);
            this.searchInput.addEventListener('keydown', this.handleSearchKeyDown);
          }
        }

        if (this.button) {
          this.button.addEventListener('click', this.handleButtonClick);
          this.button.addEventListener('keydown', this.handleButtonKeyDown);
          this.button.setAttribute('aria-expanded', 'false');
        }

        this.addEventListener('focusout', this.handleFocusOut);

        this.items.forEach((item) => {
          item.addEventListener('click', this.handleItemClick);
          item.addEventListener('keydown', this.handleItemKeyDown);
          if (!item.hasAttribute('tabindex')) {
            item.setAttribute('tabindex', '-1');
          }
        });

        this.updateSelector();
      }

      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener('click', this.handleButtonClick);
          this.button.removeEventListener('keydown', this.handleButtonKeyDown);
        }

        this.removeEventListener('focusout', this.handleFocusOut);

        if (this.searchInput) {
          this.searchInput.removeEventListener('input', this.handleSearchInput);
          this.searchInput.removeEventListener(
            'keydown',
            this.handleSearchKeyDown,
          );
        }

        this.items.forEach((item) => {
          item.removeEventListener('click', this.handleItemClick);
          item.removeEventListener('keydown', this.handleItemKeyDown);
        });
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (this.isConnected && oldValue !== newValue) {
          this.updateSelector();
        }
      }

      get visibleItems() {
        return this.items.filter((item) => !item.hidden);
      }

      handleButtonClick() {
        this.togglePanel();
      }

      handleButtonKeyDown(event) {
        switch (event.key) {
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.togglePanel();
            break;
          case 'Escape':
            this.closePanel();
            break;
          case 'ArrowDown':
            event.preventDefault();
            this.openPanel();
            if (this.searchInput) {
              this.searchInput.focus();
            } else {
              this.focusFirstItem();
            }
            break;
        }
      }

      handleItemClick(event) {
        event.preventDefault();
        const clickedItem = event.currentTarget;
        this.selectItem(clickedItem);
      }

      handleItemKeyDown(event) {
        const visibleItems = this.visibleItems;
        const currentIndex = visibleItems.indexOf(event.currentTarget);

        switch (event.key) {
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.selectItem(event.currentTarget);
            break;
          case 'Escape':
            event.preventDefault();
            this.closePanel();
            if (this.button) {
              this.button.focus();
            }
            break;
          case 'ArrowDown':
            event.preventDefault();
            if (currentIndex < visibleItems.length - 1) {
              visibleItems[currentIndex + 1].focus();
            }
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (currentIndex > 0) {
              visibleItems[currentIndex - 1].focus();
            } else if (this.searchInput) {
              this.searchInput.focus();
            }
            break;
          case 'Home':
            event.preventDefault();
            this.focusFirstItem();
            break;
          case 'End':
            event.preventDefault();
            this.focusLastItem();
            break;
        }
      }

      handleSearchInput(event) {
        this.filterItems(event.target.value);
      }

      handleSearchKeyDown(event) {
        switch (event.key) {
          case 'Escape':
            event.preventDefault();
            this.closePanel();
            if (this.button) {
              this.button.focus();
            }
            break;
          case 'ArrowDown':
            event.preventDefault();
            this.focusFirstItem();
            break;
          case 'Enter':
            event.preventDefault();
            if (this.visibleItems.length === 1) {
              this.selectItem(this.visibleItems[0]);
            }
            break;
        }
      }

      handleFocusOut(event) {
        if (
          event.relatedTarget &&
          this.contains(event.relatedTarget)
        ) {
          return;
        }

        setTimeout(() => {
          if (!this.contains(document.activeElement)) {
            this.closePanel();
          }
        }, 0);
      }

      filterItems(query) {
        const normalizedQuery = query.trim().toLowerCase();
        let visibleCount = 0;

        this.items.forEach((item) => {
          const searchText = (
            item.dataset.search || item.textContent
          ).toLowerCase();
          const matches =
            !normalizedQuery || searchText.includes(normalizedQuery);

          item.toggleAttribute('hidden', !matches);
          if (matches) visibleCount++;
        });

        if (this.emptyState) {
          this.emptyState.toggleAttribute('hidden', visibleCount > 0);
        }
      }

      resetFilter() {
        if (!this.filterable) return;

        if (this.searchInput) {
          this.searchInput.value = '';
        }

        this.filterItems('');
      }

      togglePanel() {
        const isOpen = !this.panel.hasAttribute('hidden');
        if (isOpen) {
          this.closePanel();
        } else {
          this.openPanel();
        }
      }

      openPanel() {
        this.panel.removeAttribute('hidden');
        if (this.button) {
          this.button.setAttribute('aria-expanded', 'true');
        }

        if (this.searchInput) {
          this.resetFilter();
          requestAnimationFrame(() => {
            this.searchInput.focus();
          });
        }
      }

      closePanel() {
        this.panel.setAttribute('hidden', '');
        if (this.button) {
          this.button.setAttribute('aria-expanded', 'false');
        }

        this.resetFilter();
      }

      selectItem(item) {
        const dataElement = item.dataset.value
          ? item
          : item.querySelector('[data-value]');
        if (!dataElement) return;

        const value = dataElement.dataset.value;
        const content =
          dataElement.dataset.content || dataElement.textContent.trim();

        if (!value) return;

        this.items.forEach((listItem) => {
          listItem.removeAttribute('aria-current');
          const link = listItem.querySelector('[data-value]') || listItem;
          link.removeAttribute('aria-current');
        });

        item.setAttribute('aria-current', 'true');
        if (dataElement !== item) {
          dataElement.setAttribute('aria-current', 'true');
        }

        this.input.value = value;

        if (this.button) {
          this.updateButtonText(content);
        }

        this.closePanel();
      }

      updateButtonText(content) {
        if (!this.button) return;

        const icon = this.button.querySelector('svg, .icon-caret');
        if (icon) {
          const iconHTML = icon.outerHTML;
          this.button.innerHTML = content + ' ' + iconHTML;
        } else {
          this.button.textContent = content;
        }
      }

      updateSelector() {
        this.items = Array.from(this.querySelectorAll('li.disclosure__item'));

        const currentItem = this.items.find(
          (item) => item.getAttribute('aria-current') === 'true',
        );
        if (currentItem && this.input) {
          const dataElement = currentItem.dataset.value
            ? currentItem
            : currentItem.querySelector('[data-value]');
          if (dataElement) {
            const value = dataElement.dataset.value;
            const content =
              dataElement.dataset.content || dataElement.textContent.trim();
            this.input.value = value;

            if (this.button) {
              this.updateButtonText(content);
            }
          }
        }
      }

      focusFirstItem() {
        const visibleItems = this.visibleItems;
        if (visibleItems.length > 0) {
          visibleItems[0].focus();
        }
      }

      focusLastItem() {
        const visibleItems = this.visibleItems;
        if (visibleItems.length > 0) {
          visibleItems[visibleItems.length - 1].focus();
        }
      }
    },
  );
}
